import { Money } from '../types/money';
import { Order } from 'square';

/**
 * Represents a postal address in a country. For more information, See
 * [Working with Addresses](https://developer.squareup.com/docs/build-basics/common-data-types/working-with-addresses).
 * */
type Address = {
  /**
   * The first line of the address.
   *
   * Fields that start with address that provides the address's most specific details,
   * like street number, street name, and building name. They do not provide less
   * specific details like city, state/province, or country (these details are provided
   * in other fields).
   *
   * See [Working with Addresses](https://developer.squareup.com/docs/build-basics/common-data-types/working-with-addresses)
   * for examples.
   */
  addressLine1?: string;

  /** The second line of the address, if any. */
  addressLine2?: string;

  /** The third line of the address, if any. */
  addressLine3?: string;

  /**
   * The city or town of the address. For a full list of field meanings by country,
   * see [Working with Addresses](https://developer.squareup.com/docs/build-basics/common-data-types/working-with-addresses).
   */
  locality?: string;

  /**
   * The address's postal code. For a full list of field meanings by country, see
   * [Working with Addresses](https://developer.squareup.com/docs/build-basics/common-data-types/working-with-addresses).
   */
  postalCode?: string;

  /** The address's country, in the two-letter format of ISO 3166. For example, `US` or `FR`. */
  country?: string;

  /** Optional first name when it's representing recipient. */
  firstName?: string;

  /** Optional last name when it's representing recipient. */
  lastName?: string;
};

/** Represents a customer associated with the order. */
type Customer = {
  /**
   * An optional second ID used to associate the customer profile with an entity in
   * another system.
   */
  referenceId: string;

  /** The phone number associated with the customer. */
  phoneNumber?: string;

  /** The address associated with the customer. */
  address?: Address;

  /**
   * The birthday associated with the customer, in YYYY-MM-DD format. For example,
   * 1998-09-21 represents September 21, 1998, and 0000-09-21 represents September 21
   * (without a birth year).
   */
  birthday?: string;

  /** The email address associated with the customer. */
  emailAddress?: string;

  /** The given name (that is, the first name) associated with the customer. */
  givenName?: string;

  /** The family name (that is, the last name) associated with the customer. */
  familyName?: string;
};

/**
 * Attributes used to verify the existence of a customer.
 */
type CustomerLookupAttributes = Partial<Customer> & {
  /**
   * An NFC [Apple Value Added Services](https://support.apple.com/guide/security/contactless-passes-in-apple-pay-secbd55491ad/web)
   * payload string provided by the buyer's Apple device to the buyer-facing display.
   */
  appleNfcVasPayload?: string;
};

/** Indicates the level at which the discount applies. */
enum DiscountScope {
  /** Used for reporting only. */
  OTHER_DISCOUNT_SCOPE = 'OTHER_DISCOUNT_SCOPE',

  /** The discount should be applied to only line items. */
  LINE_ITEM = 'LINE_ITEM',

  /** The discount should be applied to the entire order. */
  ORDER = 'ORDER',
}

/**
 * Indicates how the discount is applied to the associated line item or order. Note
 * that only FIXED_AMOUNT is supported for 3P add-ons at the moment.
 */
enum DiscountType {
  /**
   * Used for reporting only. The original transaction discount type is currently
   * not supported by the API.
   *
   * 3P add-ons cannot create this discount type.
   */
  UNKNOWN_DISCOUNT = 'UNKNOWN_DISCOUNT',

  /**
   * Apply the discount as a fixed percentage (such as 5%) off the item price.
   *
   * 3P add-ons cannot create this discount type.
   */
  FIXED_PERCENTAGE = 'FIXED_PERCENTAGE',

  /**
   * Apply the discount as a fixed monetary value (such as $1.00) off the item price.
   */
  FIXED_AMOUNT = 'FIXED_AMOUNT',

  /**
   * Apply the discount as a variable percentage based on the item price. The specific
   * discount percentage of a VARIABLE_PERCENTAGE discount is assigned at the time of
   * the purchase. Note that Square currently only supports FIXED_PERCENTAGE discount
   * for 3P add-ons.
   *
   * 3P add-ons cannot create this discount type.
   */
  VARIABLE_PERCENTAGE = 'VARIABLE_PERCENTAGE',

  /**
   * Apply the discount as a variable amount based on the item price. The specific
   * discount amount of a VARIABLE_AMOUNT discount is assigned at the time of the purchase.
   * Note that Square currently only supports FIXED_PERCENTAGE discount for 3P add-ons.
   *
   * 3P add-ons cannot create this discount type.
   */
  VARIABLE_AMOUNT = 'VARIABLE_AMOUNT',
}

/** Represents a discount in an order. */
interface Discount {
  /** A unique ID that identifies the discount only within this order. */
  uid?: string;

  /** The discount's name. */
  name: string;

  /** Indicates how the discount is applied to the associated line item or order. */
  type: DiscountType;

  /** Indicates the level at which the discount applies. */
  scope: DiscountScope;

  /**
   * Represents an amount of money. `Money` fields can be signed or unsigned.
   * Fields that do not explicitly define whether they are signed or unsigned are
   * considered unsigned and can only hold positive amounts. For signed fields, the
   * sign of the value indicates the purpose of the money transfer. See
   * [Working with Monetary Amounts](https://developer.squareup.com/docs/build-basics/working-with-monetary-amounts)
   * for more information.
   */
  amountMoney: Money;

  /**
   * Represents an amount of money. `Money` fields can be signed or unsigned.
   * Fields that do not explicitly define whether they are signed or unsigned are
   * considered unsigned and can only hold positive amounts. For signed fields, the
   * sign of the value indicates the purpose of the money transfer. See
   * [Working with Monetary Amounts](https://developer.squareup.com/docs/build-basics/working-with-monetary-amounts)
   * for more information.
   */
  appliedMoney?: Money;

  /**
   * The unique ID of the line item that this discount is associated with. If the
   * discount does not have a LINE_ITEM scope, this field is not defined.
   */
  lineItemId?: string;

  /**
   * Application-defined data attached to this discount. Metadata fields are intended
   * to store descriptive references or associations with an entity in another system
   * or store brief information about the object. Square does not process this field;
   * it only stores and returns it in relevant API calls. Do not use metadata to store
   * any sensitive information (such as personally identifiable information or card
   * details).
   *
   * Keys written by applications must be 60 characters or less and must be in the
   * character set `[a-zA-Z0-9_-]`. Entries can also include metadata generated by
   * Square. These keys are prefixed with a namespace, separated from the key with
   * a ':' character.
   * Values have a maximum length of 255 characters.
   * An application can have up to 10 entries per metadata field.
   * Entries written by applications are private and can only be read or modified
   * by the same application.
   * For more information, see [Metadata](https://developer.squareup.com/docs/build-basics/metadata).
   */
  metadata: Record<string, string>;
}

/** APIs related to the order. */
interface OrderApi {
  /**
   * Associates a customer with the current order. There can be at most one customer
   * associated with the current order.
   * @param customer Customer to associate the order with.
   * @throws {RequestError} if fails to associate the customer.
   */
  addCustomer: (customer: Customer) => Promise<void>;

  /**
   * Retrieves customer associated with the current order.
   * @returns Customer associated with the current order.
   * @throws {RequestError} if fails to retrieve the customer.
   */
  getCustomer: () => Promise<Customer>;

  /**
   * Removes the customer associated with the current order.
   * @throws {RequestError} if fails to remove the customer.
   */
  removeCustomer: () => Promise<void>;

  /**
   * Adds a discount to the current order.
   * @param discount Discount to add to the current order.
   * @returns The discount ID for the discount.
   * @throws {RequestError} if fails to add the discount.
   */
  addDiscount: (discount: Discount) => Promise<string>;

  /**
   * Removes a discount from the current order.
   * @param discountId ID of the discount to remove.
   * @throws {RequestError} if fails to remove the discount.
   */
  removeDiscount: (discountId: string) => Promise<void>;

  /**
   * Retrieves the current order.
   * @returns The current order.
   * @throws {RequestError} if fails to retrieve the order.
   */
  retrieve: () => Promise<Order>;
}

export {
  Address,
  Customer,
  CustomerLookupAttributes,
  Discount,
  DiscountScope,
  DiscountType,
  OrderApi,
};
export type {
  Order,
  OrderLineItem,
  OrderLineItemAppliedDiscount,
  OrderLineItemDiscount,
  OrderServiceCharge,
  Tender,
} from 'square';
